import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Download, Truck, Package as PackageIcon, StickyNote, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PACKAGE_LABEL = { pdf: "PDF", cad: "CAD", revit: "Revit" };

const DELIVERY_STYLES = {
  pending: "text-yellow-800 border-yellow-200 bg-yellow-50",
  sent: "text-blue-700 border-blue-200 bg-blue-50",
  completed: "text-emerald-700 border-emerald-200 bg-emerald-50",
  n_a: "text-muted-foreground border-border bg-background",
};

const AdminOrderDetail = () => {
  const { session_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  // Iter48 — delivery workflow local state
  const [deliveryStatus, setDeliveryStatus] = useState("pending");
  const [noteDraft, setNoteDraft] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${session_id}`);
      setData(data);
      setDeliveryStatus(data.order?.delivery_status || (data.order?.manual_delivery_required ? "pending" : "n_a"));
    } catch (e) { toast.error("Failed to load", { description: e.response?.data?.detail || "" }); }
  };

  useEffect(() => {
    (async () => { await reload(); setLoading(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_id]);

  const showInvoice = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${session_id}/invoice`);
      setInvoice(data);
      toast.success("Invoice generated");
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
  };

  const printInvoice = () => window.print();

  const updateDeliveryStatus = async (next) => {
    setSavingStatus(true);
    try {
      await api.patch(`/admin/orders/${session_id}/delivery`, { delivery_status: next });
      setDeliveryStatus(next);
      toast.success(`Delivery status set to ${next}`);
      await reload();
    } catch (e) {
      toast.error("Failed to update", { description: e.response?.data?.detail || "" });
    } finally { setSavingStatus(false); }
  };

  const addNote = async () => {
    const note = noteDraft.trim();
    if (!note) return;
    setSavingNote(true);
    try {
      await api.patch(`/admin/orders/${session_id}/delivery`, { internal_note: note });
      setNoteDraft("");
      toast.success("Note added");
      await reload();
    } catch (e) {
      toast.error("Failed to save note", { description: e.response?.data?.detail || "" });
    } finally { setSavingNote(false); }
  };

  if (loading) return <AdminLayout title="Order"><div className="text-muted-foreground">Loading…</div></AdminLayout>;
  if (!data) return <AdminLayout title="Order"><div className="text-muted-foreground">Not found.</div></AdminLayout>;

  const o = data.order;
  const lineItems = data.line_items || [];
  const notes = o.internal_notes || [];
  const deliveryRequired = !!o.manual_delivery_required;

  return (
    <AdminLayout title={`Order ${o.session_id.slice(-12)}`} actions={
      <div className="flex gap-2">
        <Link to="/admin/orders" data-testid="back-orders" className="text-sm text-muted-foreground hover:text-accent flex items-center gap-2"><ArrowLeft size={14} /> Back</Link>
        <Button data-testid="show-invoice-btn" onClick={showInvoice} variant="outline" className="rounded-none"><FileText size={14} className="mr-2" /> Invoice</Button>
      </div>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="order-detail-grid">
        <div className="lg:col-span-2 border border-border bg-card p-6">
          <div className="overline text-accent">Order details</div>
          <div className="mt-4 space-y-2 text-sm">
            <div><span className="text-muted-foreground">Session ID:</span> <code>{o.session_id}</code></div>
            <div><span className="text-muted-foreground">Gateway:</span> {o.gateway || o.provider || "stripe"}</div>
            <div><span className="text-muted-foreground">Amount:</span> {(o.currency || "").toUpperCase()} {o.amount}</div>
            <div><span className="text-muted-foreground">Payment status:</span> {o.payment_status}</div>
            <div><span className="text-muted-foreground">Created:</span> {new Date(o.created_at).toLocaleString()}</div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Delivery:</span>
              {deliveryRequired ? (
                <span data-testid="delivery-status-pill" className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border ${DELIVERY_STYLES[deliveryStatus]}`}>
                  <Truck size={10} /> {deliveryStatus === "n_a" ? "pending" : deliveryStatus}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">auto — no manual action needed</span>
              )}
            </div>
          </div>

          {/* Line items with package tier */}
          <div className="mt-6 border-t border-border pt-4">
            <div className="overline text-accent mb-3">Line items</div>
            {lineItems.length === 0 ? (
              <div className="text-sm text-muted-foreground">No line items linked.</div>
            ) : (
              <div className="divide-y divide-border" data-testid="admin-order-line-items">
                {lineItems.map((li, i) => {
                  const p = li.plan;
                  return (
                    <div key={`${li.plan_id}-${i}`} className="py-3 flex justify-between items-start gap-4" data-testid={`line-item-${i}`}>
                      <div className="text-sm flex-1">
                        <div className="font-medium">{p?.title || <span className="text-muted-foreground">Plan removed</span>}</div>
                        {p && <div className="text-xs text-muted-foreground">{p.style}</div>}
                        <div className="mt-1 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1 border border-primary/40 bg-primary/5 px-2 py-0.5">
                            <PackageIcon size={10} className="text-primary" />
                            <span className="text-[10px] uppercase tracking-widest text-primary">{PACKAGE_LABEL[li.package_tier] || li.package_tier} Package</span>
                          </span>
                          {p?.internal_reference && (
                            <span className="text-[11px] font-mono text-muted-foreground" data-testid={`line-item-ref-${i}`}>
                              Ref: {p.internal_reference}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-right">
                        {li.price != null && <>{(o.currency || "").toUpperCase()} {li.price}</>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-border bg-card p-6">
            <div className="overline text-accent">Customer</div>
            {data.customer ? (
              <div className="mt-4 text-sm">
                <div className="font-medium">{data.customer.name}</div>
                <div className="text-muted-foreground">{data.customer.email}</div>
                <Link to={`/admin/customers/${data.customer.user_id}`} className="inline-block mt-3 text-accent text-xs hover:underline" data-testid="link-to-customer">View customer →</Link>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground mt-4">Guest checkout</div>
            )}
          </div>

          {/* Iter48 — Delivery workflow panel (only for CAD/Revit orders) */}
          {deliveryRequired && (
            <div className="border border-accent/40 bg-accent/5 p-6" data-testid="delivery-panel">
              <div className="overline text-accent flex items-center gap-2"><Truck size={12} /> Manual delivery</div>
              <p className="mt-3 text-xs text-muted-foreground">
                This order contains CAD or Revit packages. Locate the source files using the reference on each line
                and email them to the customer, then advance the status below.
              </p>
              <div className="mt-4">
                <label className="overline text-muted-foreground">Status</label>
                <Select value={deliveryStatus} onValueChange={updateDeliveryStatus}>
                  <SelectTrigger data-testid="delivery-status-select" className="rounded-none h-11 mt-2" disabled={savingStatus}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <Button size="sm" variant="outline" className="rounded-none" disabled={savingStatus || deliveryStatus === "pending"} onClick={() => updateDeliveryStatus("pending")} data-testid="btn-status-pending">Pending</Button>
                <Button size="sm" variant="outline" className="rounded-none" disabled={savingStatus || deliveryStatus === "sent"} onClick={() => updateDeliveryStatus("sent")} data-testid="btn-status-sent"><Send size={12} className="mr-1" />Sent</Button>
                <Button size="sm" className="rounded-none bg-accent hover:bg-accent/90" disabled={savingStatus || deliveryStatus === "completed"} onClick={() => updateDeliveryStatus("completed")} data-testid="btn-status-completed"><CheckCircle2 size={12} className="mr-1" />Done</Button>
              </div>
            </div>
          )}

          {/* Internal notes */}
          <div className="border border-border bg-card p-6" data-testid="internal-notes-panel">
            <div className="overline text-accent flex items-center gap-2"><StickyNote size={12} /> Internal notes</div>
            <div className="mt-4 space-y-3">
              <Textarea
                data-testid="internal-note-input"
                rows={3}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="e.g. Files retrieved from NAS/CAD/2026-047, awaiting client CAD viewer license…"
                className="rounded-none"
              />
              <Button
                data-testid="internal-note-submit"
                onClick={addNote}
                disabled={savingNote || !noteDraft.trim()}
                className="rounded-none bg-primary w-full"
              >
                {savingNote ? <><Loader2 size={12} className="mr-2 animate-spin" /> Saving…</> : "Add note"}
              </Button>
            </div>
            {notes.length > 0 && (
              <div className="mt-5 border-t border-border pt-4 space-y-3 max-h-96 overflow-y-auto" data-testid="internal-notes-list">
                {[...notes].reverse().map((n) => (
                  <div key={n.id} className="text-sm border-l-2 border-accent/40 pl-3">
                    <div className="whitespace-pre-wrap">{n.note}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                      {n.author_email} · {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {invoice && (
        <div className="mt-8 border border-border bg-card p-8 max-w-3xl mx-auto print:shadow-none" data-testid="invoice-preview">
          <div className="flex justify-between items-start">
            <div>
              <div className="overline text-accent">Invoice</div>
              <div className="font-display text-3xl mt-1">{invoice.invoice_no}</div>
            </div>
            <Button data-testid="print-invoice" onClick={printInvoice} className="rounded-none bg-accent hover:bg-accent/90 print:hidden">
              <Download size={14} className="mr-2" /> Print / Save PDF
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
            <div>
              <div className="overline text-muted-foreground">From</div>
              <div className="font-medium">{invoice.company}</div>
            </div>
            <div>
              <div className="overline text-muted-foreground">To</div>
              <div className="font-medium">{invoice.customer_email || "Guest"}</div>
            </div>
          </div>
          <table className="w-full mt-6 text-sm border-t border-border">
            <thead><tr className="text-left"><th className="py-2 overline text-muted-foreground">Item</th><th className="py-2 overline text-muted-foreground text-right">Amount</th></tr></thead>
            <tbody>
              {invoice.line_items.map((li, i) => (
                <tr key={`${li.title}-${i}`} className="border-t border-border">
                  <td className="py-3">{li.title}</td>
                  <td className="py-3 text-right">{invoice.currency?.toUpperCase()} {li.amount}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border font-medium">
                <td className="py-3">Total</td>
                <td className="py-3 text-right">{invoice.currency?.toUpperCase()} {invoice.amount}</td>
              </tr>
            </tfoot>
          </table>
          <div className="mt-4 text-xs text-muted-foreground">Payment via {invoice.gateway} · {invoice.payment_status}</div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrderDetail;
